import axios from 'axios';
import { useContext, useState, useEffect } from 'react';
import { FieldInput } from 'components/fieldInput';
import { Pagination } from 'components/Pagination';
import {
  Animate,
  Grid,
  Search,
  Table,
  useAPI,
  useNavigate,
  ViewContext,
} from 'components/lib';

import DateUtils from 'helpers/dateUtils';
import InputUtils from 'helpers/inputUtils';

import styles from './styles.module.scss';

export function ServiceCenter() {
  const navigate = useNavigate();
  const context = useContext(ViewContext);
  const user = useAPI('/api/user');

  const dateUtils = new DateUtils();
  const inputUtils = new InputUtils();

  const [payload, setPayload] = useState();
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [searchQueryString, setSearchQueryString] = useState('');
  const [statusFilter, setStatusFilter] = useState('Active');
  const [ticketStatusFilter, setTicketStatusFilter] = useState([]);
  const [agencyId, setAgencyId] = useState();

  useEffect(() => {
    if (user?.data?.account_id) {
      setAgencyId(user?.data?.account_id);
    }
  }, [user]);

  async function fetchTickets(page, search, status, ticketStatus) {
    if (!agencyId) return;

    setLoading(true);
    await axios
      .get(`/api/tickets/${agencyId}`, {
        params: {
          page,
          search,
          status,
          ticketStatus,
          sortBy: 'createdAt',
          sortOrder: 'desc',
        },
      })
      .then((response) => {
        if (response.data && response.data.success) {
          setPayload(response.data);
          const formattedTickets = response.data.data.map(
            (ticket) => ({
              id: ticket.id,
              Number: ticket.ticketNumber,
              'Agency name': ticket.agencyName
                ? ticket.agencyName
                : 'N/A',
              'Company Name':
                ticket.companyName &&
                inputUtils
                  .capitalizeFirstLetter(ticket.companyName)
                  .replaceAll('Llc', 'LLC'),
              'Policy Number': ticket.policyNumber || ticket.submissionNumber
                ? ticket.policyNumber || ticket.submissionNumber
                : ticket.id,
              'Created At':
                ticket.createdAt &&
                dateUtils.timestamp(ticket.createdAt),
              'Updated At':
                ticket.updatedAt &&
                dateUtils.timestamp(ticket.updatedAt),
              Status: ticket.ticketStatus,
              messages: ticket.messages,
              ticketStatus: ticket.ticketStatus,
            })
          );
          setTickets(formattedTickets);
        } else {
          context.notification.show(
            'Unexpected API response structure',
            'error',
            true,
            'toast',
            'x-circle'
          );
        }
      })
      .catch(() => {
        context.notification.show(
          'Failed to load tickets',
          'error',
          true,
          'toast',
          'x-circle'
        );
      })
      .finally(() => {
        setLoading(false);
      });
  }

  useEffect(() => {
    if (agencyId) {
      fetchTickets(
        page,
        searchQueryString,
        statusFilter,
        ticketStatusFilter
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    page,
    searchQueryString,
    statusFilter,
    ticketStatusFilter,
    agencyId,
  ]);

  const handleStatusFilter = (status) => {
    setStatusFilter(status);
    setPage(1);
  };

  return (
    <Animate type="pop">
      <div
        style={{
          display: 'flex',
          alignContent: 'center',
          justifyContent: 'space-between',
        }}
      >
        <div style={{ display: 'flex' }}>
          {['Active', 'Finished'].map((status) => (
            <div
              key={status}
              style={{
                marginLeft: '5px',
                width: '150px',
                backgroundColor:
                  statusFilter === status ? 'black' : '#EAEAEA',
                color:
                  statusFilter === status ? 'white' : '#090E3480',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: 10,
                fontSize: 16,
                fontWeight: 600,
                borderTopLeftRadius: 10,
                borderTopRightRadius: 10,
                cursor: 'pointer',
              }}
              onClick={() => handleStatusFilter(status)}
            >
              {status === 'Finished' ? 'Completed' : status}
            </div>
          ))}
        </div>
      </div>
      <Grid cols="1">
        <div className={styles.wrapper} style={{ marginTop: 0 }}>
          <div
            style={{
              marginBottom: 10,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <div style={{ width: '80%', marginTop: 30 }}>
              <Search
                callback={(query) => {
                  setSearchQueryString(query);
                  setPage(1);
                }}
              />
            </div>
            <div style={{ width: '20%', marginLeft: 20 }}>
              <FieldInput
                id="ticketStatus_filter"
                type="multi_select"
                label="Filters"
                defaultValue={[]}
                options={[
                  { value: 'Pending', label: 'Pending' },
                  { value: 'In Progress', label: 'In Progress' },
                ]}
                handleChange={(ticketStatus) => {
                  setTicketStatusFilter(
                    ticketStatus.map((status) => {
                      return status.value;
                    })
                  );
                }}
              />
            </div>
          </div>

          <Table
            data={tickets}
            loading={loading}
            badge={{
              col: 'Status',
              color: 'gray',
              condition: [
                { value: 'Pending', color: 'gray' },
                { value: 'In Progress', color: 'blue' },
                { value: 'Completed', color: 'green' },
              ],
            }}
            hide={[
              'id',
              'assignedUserEmail',
              'userId',
              'ticketStatus',
              'messages',
            ]}
            actions={{
              custom: [
                {
                  icon: 'search',
                  color: '#637381',
                  action: (row) => {
                    if (row.ticketStatus.length > 0) {
                      navigate(
                        `/app/service_center/ticket/${row.id}`
                      );
                    } else {
                      context.notification.show(
                        'This ticket is being registered by the admin and cannot be accessed yet.',
                        'error',
                        true,
                        'toast',
                        'x-circle'
                      );
                    }
                  },
                },
              ],
            }}
          />
          {payload && !loading && (
            <Pagination
              pagesCount={payload.pages}
              active={payload.page}
              onChange={(newPage) => {
                setPage(newPage);
              }}
            />
          )}
        </div>
      </Grid>
    </Animate>
  );
}
